import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { dateToNumber } from 'lib/riskbase';
import { cloneDeep } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { GlobalFieldLightRepeatableInstanceScreen } from 'screens/FieldComponents/GlobalFieldLightRepeatableInstanceScreen';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doAseFieldInstanceSave } from 'store/assessments/actions';
import {
  selectAseFieldInstanceAse,
  selectAseFieldInstanceAst,
  selectAseAssetId,
  selectAseFieldInstancesForGroup,
  selectAseTplFieldGroup,
  selectAseTemplateFieldFieldsConfig,
  selectAseIsEditable,
  selectAseId,
  selectAseIsOnline,
  selectAseFieldInstanceAseMerged,
  selectAseDataFieldFields,
} from 'store/assessments/selectors';
import { AseFieldInstanceWithFormattedAutoId } from 'store/assessments/types';
import { FieldTypes } from 'store/assets/types';
import { PinNew } from 'store/plan/types';
import { AppContextEnum, FieldResult } from 'types/types';
import { v4 as uuid } from 'uuid';

type ModalProps = ModalScreenProps<ModalScreens.AseLightRepeatableInstance>;

export const LightRepeatableInstanceModal: React.FC<ModalProps> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const isDirty = React.useRef(false);

  const { groupId, instanceId, parent } = route.params;

  const assessmentId = useSelector(selectAseId);
  const isOnline = useSelector(selectAseIsOnline);

  // Select group and fields info
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const fields = useSelector(selectAseDataFieldFields);
  const fieldConfigs = useSelector(selectAseTemplateFieldFieldsConfig);

  const isEditable = useSelector(selectAseIsEditable);
  const assetId = useSelector(selectAseAssetId);

  const canSave = !!(
    useSelector((state) =>
      selectAseFieldInstanceAseMerged(state, parent.instanceId || '-1'),
    ) || !parent.instanceId
  );

  // Select instances
  const assessmentInstance = useSelector((state) =>
    selectAseFieldInstanceAse(state, instanceId || '-1'),
  );
  const assetInstance = useSelector((state) =>
    selectAseFieldInstanceAst(state, instanceId || '-1'),
  );
  const allInstances = useSelector((state) =>
    selectAseFieldInstancesForGroup(state, {
      groupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );

  const instance = React.useRef<AseFieldInstanceWithFormattedAutoId>(
    cloneDeep(
      assetInstance && assessmentInstance
        ? {
            ...assetInstance,
            updatedTs: assessmentInstance.updatedTs,
            results: {
              ...assetInstance.results,
              ...assessmentInstance.results,
            },
          }
        : assetInstance
          ? { ...assetInstance }
          : assessmentInstance
            ? { ...assessmentInstance }
            : {
                instanceId: uuid(),
                groupId,
                createdTs: dateToNumber(),
                updatedTs: dateToNumber(),
                results: {},
                action: null,
                inAssessment: true,
                // autoId not applicable to light groups
                autoId: 0,
                draftAutoId: 0,
                formattedAutoId: '',
                checks: {},
                draft: true,
              },
    ),
  );

  const resultsAssetRef = React.useRef<Record<string, FieldResult>>({
    ...(assetInstance?.results || {}),
  });
  const resultsAssessmentRef = React.useRef<Record<string, FieldResult>>({
    ...(assessmentInstance?.results || {}),
  });

  const dirtyResultsRef = React.useRef<Record<string, FieldResult>>({});

  const checkIsDirty = () => !!Object.keys(dirtyResultsRef.current).length;
  isDirty.current = checkIsDirty();

  const otherInstances = React.useMemo(
    () =>
      instanceId
        ? Object.values(allInstances).filter(
            ({ instanceId: otherInstanceId }) => otherInstanceId !== instanceId,
          )
        : Object.values(allInstances),
    [instanceId, allInstances],
  );

  const handleGoBack = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleGoBackWithConfirm = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onDiscard: handleGoBack,
        onSave: handleSave,
      });
      return;
    }
    handleGoBack();
  };

  const handleSave = React.useCallback(() => {
    dispatch(
      doAseFieldInstanceSave({
        instance: {
          ...instance.current,
          results: { ...dirtyResultsRef.current },
        },
        parent,
        isAdding: !assessmentInstance,
        isLite: true,
        newLiteInstances: {},
      }),
    );
  }, [assessmentInstance, dispatch, parent]);

  const handleChange = React.useCallback(
    (
      fieldId: string,
      value: string | string[] | null,
      pin?: PinNew | '' | null,
    ) => {
      const field = fields[fieldId];
      if (!field) {
        return;
      }
      let newResult: FieldResult | undefined;
      let removeResult: boolean = false;
      if (field.type === FieldTypes.PIN) {
        if (pin === null) {
          // Null pins remove the assessment's value and revert to the asset's value
          delete instance.current.results[fieldId];
          delete resultsAssessmentRef.current[fieldId];
          newResult = assetInstance?.results[fieldId];
          removeResult = true;
        } else {
          newResult = {
            fieldId,
            ts: dateToNumber(),
            value: '',
            pin: pin || '',
            action: null,
          };
          resultsAssessmentRef.current = {
            ...resultsAssessmentRef.current,
            [fieldId]: { ...newResult },
          };
        }
      } else if (value === null) {
        // Null values remove the assessment's value and revert to the asset's value
        delete instance.current.results[fieldId];
        delete resultsAssessmentRef.current[fieldId];
        newResult = assetInstance?.results[fieldId];
        removeResult = true;
      } else {
        newResult = {
          fieldId,
          ts: dateToNumber(),
          value: value || value === '0' ? value : '',
          pin: '',
          action: null,
        };
        resultsAssessmentRef.current = {
          ...resultsAssessmentRef.current,
          [fieldId]: { ...newResult },
        };
      }
      if (newResult) {
        instance.current = {
          ...instance.current,
          results: {
            ...instance.current.results,
            [fieldId]: { ...newResult },
          },
        };
      }
      dirtyResultsRef.current = {
        ...dirtyResultsRef.current,
        [fieldId]: {
          fieldId,
          value: value !== null ? value : '',
          pin: pin || '',
          ts: dateToNumber(),
          action: removeResult ? 'delete' : null,
        },
      };
      instance.current.updatedTs = dateToNumber();
      forceUpdate();
    },
    [assetInstance?.results, forceUpdate, fields],
  );

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleGoBackWithConfirm);

  const isBeingOpenInvalid =
    instanceId && !assetInstance && !assessmentInstance;
  React.useEffect(() => {
    if (isBeingOpenInvalid) {
      handleGoBack();
    }
  }, [handleGoBack, isBeingOpenInvalid]);

  if (!group) {
    return null;
  }

  const isNew = !assetInstance && !assessmentInstance;

  const { editableFieldIds, hiddenFieldIds } = group.fieldIds.reduce<{
    editableFieldIds: string[];
    hiddenFieldIds: string[];
  }>(
    (acc, fieldId) => {
      const fieldConfig = fieldConfigs[fieldId];
      if (!fieldConfig) {
        acc.hiddenFieldIds.push(fieldId);
      } else if (isEditable && fieldConfig.edit) {
        const field = fields[fieldId];
        if (
          field &&
          (!isOnline ||
            ![
              FieldTypes.IMAGE,
              FieldTypes.FILE,
              FieldTypes.PIN,
              FieldTypes.GROUP,
              FieldTypes.GROUPLIGHT,
            ].includes(field.type))
        ) {
          acc.editableFieldIds.push(fieldId);
        }
      }
      return acc;
    },
    {
      editableFieldIds: [],
      hiddenFieldIds: [],
    },
  );

  return (
    <GlobalFieldLightRepeatableInstanceScreen
      context={{
        context: isOnline
          ? AppContextEnum.remoteAssessment
          : AppContextEnum.localAssessment,
        assetId,
        assessmentId,
      }}
      editableFieldIds={canSave ? editableFieldIds : []}
      hiddenFieldIds={hiddenFieldIds}
      requiredFieldIds={[]}
      // editableFieldIds={isEditable ? group.fieldIds : []}
      id={instance.current.instanceId}
      isNew={isNew}
      results={instance.current.results}
      resultsOld={assetInstance ? resultsAssetRef.current : undefined}
      resultsNew={assetInstance ? resultsAssessmentRef.current : undefined}
      group={group}
      fields={fields}
      otherInstances={otherInstances}
      onPressSaveButton={isEditable && canSave ? handleSave : undefined}
      onPressCancelButton={handleGoBackWithConfirm}
      onChange={handleChange}
      showInvalid={false}
    />
  );
};
